/**
 * Capa de acceso a la API real de Pulso17 (https://www.pulso17.com/api).
 *
 * Este archivo es el único lugar que debería usar `fetch` directamente.
 * El resto de los servicios (`authService` y, más adelante,
 * `formService`/`submissionService`/`userService` ya conectados a la
 * API real) deben apoyarse en `get`/`post`/`put`/`del` de acá para
 * mantener headers, token de autenticación y manejo de errores
 * consistentes en un solo lugar.
 */

export const API_BASE_URL = 'https://api.pulso17.com/api';
export const API_LOGIN_URL = `${API_BASE_URL}/login`;
export const API_LOGOUT_URL = `${API_BASE_URL}/logout`;
export const API_URL = `${API_BASE_URL}/v1/`;

const TOKEN_KEY = 'pulso17.token';
const REQUEST_TIMEOUT_MS = 15000;

/* ------------------------------------------------------------------ */
/* Token de autenticación (Bearer)                                    */
/* ------------------------------------------------------------------ */

/** Guarda el token devuelto por `POST /login` para futuras peticiones. */
export function setAuthToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

/** Token guardado actualmente, o `null` si no hay sesión. */
export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/** Elimina el token guardado (logout). */
export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/* ------------------------------------------------------------------ */
/* Errores                                                            */
/* ------------------------------------------------------------------ */

const DEFAULT_ERROR_MESSAGES = {
  0: 'No se pudo conectar con el servidor. Revisa tu conexión.',
  401: 'Usuario o contraseña incorrectos.',
  403: 'No tienes permisos para realizar esta acción.',
  404: 'El recurso solicitado no existe.',
  422: 'Revisa los datos ingresados.',
  500: 'Error interno del servidor. Intenta más tarde.',
};

/**
 * Error enriquecido con el status HTTP y, si el backend los envía
 * (ej. Laravel), los errores de validación por campo.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, errors = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

function extractErrorMessage(status, body) {
  const firstFieldMessage = body?.errors && Object.values(body.errors)[0]?.[0];
  return (
    body?.message ||
    firstFieldMessage ||
    DEFAULT_ERROR_MESSAGES[status] ||
    'Ocurrió un error inesperado.'
  );
}

/* ------------------------------------------------------------------ */
/* Cliente HTTP genérico                                              */
/* ------------------------------------------------------------------ */

/**
 * Ejecuta una petición HTTP y devuelve el JSON ya parseado. Adjunta el
 * header `Authorization: Bearer <token>` automáticamente salvo que se
 * pase `withAuth: false` (útil para endpoints públicos como `/login`).
 *
 * @param {string} url URL completa del endpoint.
 * @param {Object} [config]
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} [config.method='GET']
 * @param {Object} [config.body] cuerpo a enviar como JSON.
 * @param {boolean} [config.withAuth=true]
 */
async function request(url, { method = 'GET', body, withAuth = true } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (withAuth) {
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch {
    throw new ApiError(DEFAULT_ERROR_MESSAGES[0], { status: 0 });
  } finally {
    clearTimeout(timeoutId);
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const responseBody = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new ApiError(extractErrorMessage(response.status, responseBody), {
      status: response.status,
      errors: responseBody?.errors ?? null,
    });
  }

  return responseBody;
}

export const get = (url, config) => request(url, { ...config, method: 'GET' });
export const post = (url, body, config) => request(url, { ...config, method: 'POST', body });
export const put = (url, body, config) => request(url, { ...config, method: 'PUT', body });
export const del = (url, config) => request(url, { ...config, method: 'DELETE' });

/** Arma una URL bajo `API_URL` (v1), ej. `apiUrl('forms')` → `.../v1/forms`. */
export function apiUrl(path = '') {
  return `${API_URL}${String(path).replace(/^\/+/, '')}`;
}

/* ------------------------------------------------------------------ */
/* Endpoints: autenticación                                           */
/* ------------------------------------------------------------------ */

/**
 * `POST /login`
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{
 *   token: string,
 *   user: { id: number, name: string, email: string, roles: string[] }
 * }>}
 */
export function login(email, password) {
  return post(API_LOGIN_URL, { email, password }, { withAuth: false });
}

/** `POST /logout` — invalida el token en el backend (best-effort). */
export function logout() {
  return post(API_LOGOUT_URL, undefined);
}

/* ------------------------------------------------------------------ */
/* Endpoints: encuestas (surveys)                                     */
/* ------------------------------------------------------------------ */

/**
 * `GET /v1/surveys`
 *
 * @returns {Promise<{ data: Array<{
 *   id: number,
 *   name: string,
 *   description: string,
 *   phase: number,
 *   version: string,
 *   active: boolean,
 *   start_date: string|null,
 *   end_date: string|null,
 *   created_at: string,
 * }> }>}
 */
export function getSurveys() {
  return get(apiUrl('surveys'));
}

/**
 * `GET /v1/surveys/:id` — incluye `questions` (con sus `options`), lo
 * necesario para construir el formulario de captura dinámico.
 *
 * @param {string|number} id
 */
export function getSurveyDetail(id) {
  return get(apiUrl(`surveys/${id}`));
}

/**
 * `GET /v1/surveys/:slug` — variante por `slug` usada por los
 * formularios de tipo fijo (registro de vivienda, resultado de la
 * visita, etc., ver `models/formTypes.js`). Responde
 * `{ success, message, data }`; `data` incluye `questions` con su
 * propio esquema (`key`, `conditions` por `question_key`, etc.), ver
 * `dynamicFormService.js`.
 *
 * @param {string} slug
 */
export function getSurveyBySlug(slug) {
  return get(apiUrl(`surveys/${slug}`));
}

/**
 * `GET /v1/houses/:houseId/workflow` — indica el siguiente paso
 * pendiente para una vivienda (ej. qué formulario dinámico hay que
 * llenar después de una visita). Ver `houseWorkflowService.js`.
 *
 * @param {string|number} houseId
 */
export function getHouseWorkflow(houseId) {
  return get(apiUrl(`houses/${houseId}/workflow`));
}

/* ------------------------------------------------------------------ */
/* Endpoints: visitas a vivienda (houses/:id/visits)                  */
/* ------------------------------------------------------------------ */

/**
 * `POST /v1/houses/:houseId/visits` — registra el resultado de una
 * visita a una vivienda (formulario dinámico `visit-result`). Ver
 * `visitService.js` para el armado del payload y la cola offline.
 *
 * @param {string|number} houseId
 * @param {Object} visit
 */
export function createHouseVisit(houseId, visit) {
  return post(apiUrl(`houses/${houseId}/visits`), visit);
}

/**
 * `PUT /v1/houses/:houseId/surveys/:surveySlug` — guarda las
 * respuestas de un paso del flujo de una vivienda (formulario
 * dinámico identificado por `survey_slug`, ej. "conversation"). Ver
 * `houseSurveyService.js` para la cola offline.
 *
 * @param {string|number} houseId
 * @param {string} surveySlug
 * @param {{ answers: Record<string, any> }} payload
 */
export function createHouseSurvey(houseId, surveySlug, payload) {
  return put(apiUrl(`houses/${houseId}/surveys/${surveySlug}`), payload);
}

/**
 * `GET /v1/houses/:houseId/surveys` — devuelve las encuestas
 * contestadas para una vivienda, cada una con su `survey_slug` y
 * `answers`. Se usa en `HouseAnswersView` para mostrar un resumen de
 * todas las respuestas guardadas.
 *
 * @param {string|number} houseId
 */
export function getHouseSurveys(houseId) {
  return get(apiUrl(`houses/${houseId}/surveys`));
}

/* ------------------------------------------------------------------ */
/* Endpoints: captura de respuestas (captures)                        */
/* ------------------------------------------------------------------ */

/**
 * `POST /v1/captures/sync` — sube en lote las capturas guardadas
 * localmente (offline-first: el operativo puede llenar varias
 * encuestas sin conexión y sincronizarlas después).
 *
 * @param {Array<Object>} captures ver `captureService.buildCapturePayload`.
 */
export function syncCaptures(captures) {
  return post(apiUrl('captures/sync'), { captures });
}

/* ------------------------------------------------------------------ */
/* Endpoints: registro de vivienda (houses)                            */
/* ------------------------------------------------------------------ */

/**
 * `POST /v1/houses` — registra una vivienda (formulario dinámico
 * `house-registration`). Ver `houseCaptureService.js` para el armado
 * del payload y la cola offline.
 *
 * @param {Object} house ver `houseCaptureService.buildHousePayload`.
 */
export function createHouse(house) {
  return post(apiUrl('houses'), house);
}

/**
 * `GET /v1/houses` — lista de viviendas registradas por el operativo,
 * con su dirección y contadores de visitas/encuestas. Ver
 * `houseService.js` para la normalización.
 */
export function getHouses() {
  return get(apiUrl('houses'));
}